/**
 * useReviewsFeed — "the AI review you asked for has finished".
 *
 * An eligibility review takes minutes. Before this feed the ONLY way to learn
 * it had landed was to still be sitting on that lead's profile, whose card
 * polls every 4s — so in practice a recruiter started a review, went to do
 * something else, and never found out. This is the corner card that reaches
 * them wherever they are in the CRM, plus an OS notification for when the tab
 * isn't even in front.
 *
 * Scope is the REQUESTER (the server filters on `requested_by`): the answer is
 * owed to whoever asked the question. Failed runs ride the same feed — a review
 * that died used to be the quietest outcome of all.
 *
 * Nothing here is the system of record. The durable surface is Today's "AI
 * reviews ready" lane, which holds until someone signs the review off; this
 * card is allowed to lapse, be dismissed, or never be seen at all.
 *
 * Dismissal persists in localStorage rather than only in memory: the server
 * window is an hour, so an in-memory-only dismissal would put the same card
 * back on the next refresh. It is per-browser and best-effort by design — a
 * private window or cleared storage just means one more nudge.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { fireOsNote } from './alertKinds';

/** Matches the poll the alerts feed already pays for. */
const POLL_MS = 60_000;
const DISMISSED_KEY = 'crm.reviewCards.dismissed.v1';
/** Ids kept in storage. Far beyond an hour's worth of reviews. */
const DISMISSED_CAP = 100;

export interface ReviewReadyCard {
  /** The review id — stable, so the queue keeps the card's served time. */
  id: string;
  contactId: string;
  contactName: string | null;
  studyTitle: string | null;
  status: 'completed' | 'failed';
  overallResult: 'eligible' | 'excluded' | 'review_required' | 'incomplete' | null;
  /** Criteria the AI flagged that nobody has verified yet. */
  pendingCount: number;
  completedAt: string | null;
}

interface ReadyReviewRow {
  review_id: string;
  contact_id: string;
  contact_name: string | null;
  study_title: string | null;
  status: string;
  overall_result: string | null;
  pending_count: number;
  completed_at: string | null;
}

export interface ReviewsFeed {
  cards: ReviewReadyCard[];
  dismiss: (id: string) => void;
  open: (card: ReviewReadyCard) => void;
}

/** Verdict wording — the profile card's labels, so one review reads the same
 *  in the corner, on the lead and on Today. */
export const resultLabel = (card: ReviewReadyCard): string => {
  if (card.status === 'failed') return "Couldn't finish";
  switch (card.overallResult) {
    case 'eligible': return 'Potentially eligible';
    case 'excluded': return 'Excluded';
    case 'review_required': return 'Needs review';
    default: return 'Incomplete';
  }
};

function readDismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function persistDismissed(ids: Set<string>) {
  try {
    // Newest wins if we ever overflow: slice from the END of insertion order.
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-DISMISSED_CAP)));
  } catch {
    /* storage full / blocked — the in-memory set still holds for this session */
  }
}

export function useReviewsFeed(): ReviewsFeed {
  const navigate = useNavigate();
  const [cards, setCards] = useState<ReviewReadyCard[]>([]);
  // Reviews already announced (so a re-fetch never re-notifies) and reviews the
  // user waved off (kept hidden across refreshes AND reloads).
  const notifiedRef = useRef<Set<string>>(new Set());
  const dismissedRef = useRef<Set<string>>(readDismissed());

  const fetchReady = useCallback(async () => {
    try {
      const res = await api.get<{ reviews: ReadyReviewRow[] }>('/api/crm/eligibility-reviews/ready');
      const list: ReviewReadyCard[] = (res.data?.reviews || [])
        .filter((r) => !dismissedRef.current.has(r.review_id))
        .map((r) => ({
          id: r.review_id,
          contactId: r.contact_id,
          contactName: r.contact_name,
          studyTitle: r.study_title,
          status: r.status === 'failed' ? 'failed' : 'completed',
          overallResult: (r.overall_result as ReviewReadyCard['overallResult']) ?? null,
          pendingCount: Number(r.pending_count) || 0,
          completedAt: r.completed_at,
        }));
      for (const card of list) {
        if (notifiedRef.current.has(card.id)) continue;
        notifiedRef.current.add(card.id);
        // No chime: this is news, not an interruption — the chime belongs to a
        // lead who is waiting on the phone.
        fireOsNote({
          title: card.status === 'failed' ? 'AI review failed' : 'AI review ready',
          body: `${card.contactName || 'A lead'}${card.studyTitle ? ` — ${card.studyTitle}` : ''}: ${resultLabel(card)}`,
          tag: `crm-review:${card.id}`,
        });
      }
      setCards(list);
    } catch {
      /* a missed poll is harmless — the next one reconciles */
    }
  }, []);

  useEffect(() => {
    fetchReady();
    const id = window.setInterval(fetchReady, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchReady]);

  const dismiss = useCallback((id: string) => {
    dismissedRef.current.add(id);
    persistDismissed(dismissedRef.current);
    setCards((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const open = useCallback((card: ReviewReadyCard) => {
    dismiss(card.id);
    // A failed review has no results page to open — the lead's profile card is
    // where the failure and the "try again" live.
    navigate(card.status === 'failed'
      ? `/crm/contacts/${card.contactId}`
      : `/crm/contacts/${card.contactId}/reviews/${card.id}`);
  }, [dismiss, navigate]);

  return { cards, dismiss, open };
}

export default useReviewsFeed;
